import React, { useState, useCallback, useContext } from 'react';
import { TeamPlanContext } from '../../App';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Alert, Snackbar, Tooltip, IconButton, Paper, Chip } from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import PrintIcon from '@mui/icons-material/Print';
import HistoryIcon from '@mui/icons-material/History';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import BuildIcon from '@mui/icons-material/Build';

import SessionSetup    from './SessionSetup';
import TrackMap        from './TrackMap';        // usata solo per il PDF
import TrackMapLeaflet from './TrackMapLeaflet'; // vista interattiva
import SectorTable     from './SectorTable';
import CoachingPanel   from './CoachingPanel';
import DriverFeedback  from './DriverFeedback';
import TelemetryCharts from './TelemetryCharts';
import SessionHistory  from './SessionHistory';
import AiDisclaimerDialog, { hasAcceptedDisclaimer } from './AiDisclaimerDialog';

import { parseCsv, recoach } from '../../utils/raceEngineerApi';
import { getAuth } from 'firebase/auth';
import { logAudit, AUDIT_ACTIONS } from '../../utils/auditLog';
import { upsertReTrackFromAnalysis, getReTrack, trackNameToSlug } from '../../utils/reTracksService';
import { saveRaceSession, getRecentSessions, buildHistoryContext, lapTimeToSeconds } from '../../utils/reSessionsService';
import { fetchAiQuota, incrementAiUsage } from '../../utils/aiQuota';
import AiQuotaExceededDialog from './AiQuotaExceededDialog';

// Flag manutenzione: quando true il modulo Race Engineer è bloccato dietro
// una schermata di manutenzione. Default ON in produzione; per spegnerlo in
// locale crea un file .env.local nella root del progetto con:
//   REACT_APP_RACE_ENGINEER_MAINTENANCE=false
// Poi riavvia `npm start` (CRA carica .env.local automaticamente).
// Quando il bug telemetria è risolto in produzione, basta rimuovere questa
// costante e cancellare il return early più in basso.
const RACE_ENGINEER_MAINTENANCE =
  process.env.REACT_APP_RACE_ENGINEER_MAINTENANCE !== 'false';

export default function RaceEngineerPage({ teamId }) {
  const { t, i18n } = useTranslation('race_engineer');
  const { isTrial } = useContext(TeamPlanContext);
  const [trackData, setTrackData]           = useState(null);
  const [coaching, setCoaching]             = useState(null);
  const [coachingContext, setCoachingContext] = useState(null);
  const [weather, setWeather]               = useState(null);
  const [sessionMeta, setSessionMeta]       = useState(null);
  const [linkedSetupA, setLinkedSetupA]     = useState(null);
  const [linkedSetupB, setLinkedSetupB]     = useState(null);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [historyOpen, setHistoryOpen]       = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [pendingCoachType, setPendingCoachType] = useState(null); // 'guida' | 'setup'
  const [isCoachingGuida, setIsCoachingGuida] = useState(false);
  // Quota AI: dialog se esaurita
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [isCoachingSetup, setIsCoachingSetup] = useState(false);
  const [activeSector, setActiveSector]     = useState(null);
  const [telemetry, setTelemetry]           = useState(null);
  const [hoveredPoint, setHoveredPoint]     = useState(null);
  const [pinnedPoint, setPinnedPoint]       = useState(null);
  const [activeLapNums, setActiveLapNums]   = useState(new Set());
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState(null);
  const [previewTrackGps, setPreviewTrackGps] = useState(null); // mappa prima del CSV

  // ── Distanza Haversine in km tra due coordinate ──────────────
  const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
            + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
            * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // ── Pre-carica GPS trace dal database quando si seleziona la pista ──
  const handleTrackSelect = useCallback((track) => {
    if (!track?.nome) { setPreviewTrackGps(null); return; }
    const slug = trackNameToSlug(track.nome);
    getReTrack(slug).then(reTrack => {
      if (reTrack?.gps_trace?.length > 10) {
        setPreviewTrackGps(reTrack.gps_trace);
      } else {
        setPreviewTrackGps(null);
      }
    }).catch(() => {});
  }, []);

  // ── Step 1: carica sessione → mappa + settori subito ──────────
  const handleLoad = useCallback(async ({ csv, splitReport, referenceCsv, referenceSplit, track, vehicle, driver, setupA, setupB }) => {
    setIsLoading(true);
    setError(null);
    setTrackData(null);
    setPreviewTrackGps(null);
    setCoaching(null);
    setCoachingContext(null);
    setWeather(null);
    setActiveSector(null);
    setTelemetry(null);
    setHoveredPoint(null);
    setPinnedPoint(null);
    setActiveLapNums(new Set());
    setLinkedSetupA(setupA ?? null);
    setLinkedSetupB(setupB ?? null);

    try {
      // ── Check quota AI: blocca se trial/piano ha esaurito le analisi ──
      const quota = await fetchAiQuota(teamId);
      if (quota && !quota.isUnlimited && quota.remaining <= 0) {
        setIsLoading(false);
        setQuotaInfo(quota);
        setQuotaDialogOpen(true);
        return;
      }

      const result = await parseCsv({ csv, splitReport, referenceCsv, referenceSplit });

      // Incrementa il counter solo dopo successo parsing
      incrementAiUsage(teamId).catch(() => {/* non blocking */});

      // ── Check coerenza GPS: il CSV corrisponde alla pista selezionata? ──
      if (track?.lat && track?.lon && result.track_data?.track_gps?.length > 10) {
        const gps    = result.track_data.track_gps;
        const csvLat = gps.reduce((s, p) => s + p.lat, 0) / gps.length;
        const csvLon = gps.reduce((s, p) => s + p.lon, 0) / gps.length;
        const dist   = haversineKm(track.lat, track.lon, csvLat, csvLon);
        if (dist > 30) {
          throw new Error(
            t('setup.gps_mismatch_error', { track: track.nome, dist: Math.round(dist) })
          );
        }
      }

      setTrackData(result.track_data);
      setCoachingContext(result.coaching_context ?? null);
      setWeather(result.weather ?? null);
      setTelemetry(result.telemetry?.series?.length ? result.telemetry : null);
      if (result.session) {
        const driverName = driver?.nomeCompleto || result.session.racer;
        setSessionMeta({
          vehicle:    vehicle?.modello || result.session.vehicle,
          date:       result.session.date,
          track:      track?.nome || result.track_data?.track,
          driverName,
        });
      }

      // Salva GPS + settori su re_tracks.
      // REGOLA: usa il nome della pista selezionata SOLO se il GPS check
      // è già passato (track.lat && track.lon garantiscono la coerenza).
      // Se la pista non ha coordinate, usa lo slug auto-rilevato dal CSV
      // per evitare di contaminare re_tracks della pista sbagliata.
      if (result.track_data?.track_gps?.length > 10) {
        const gpsVerified = track?.lat && track?.lon; // check già superato senza errore
        const slug = gpsVerified
          ? trackNameToSlug(track.nome)
          : trackNameToSlug(result.track_data.track);
        const name = gpsVerified ? track.nome : result.track_data.track;
        upsertReTrackFromAnalysis(slug, name, result.track_data).catch(() => {});
      }
    } catch (err) {
      setError(err.message ?? t('page.loading_error'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Helper: costruisce lista setup e contesto storico ─────────
  const buildCoachArgs = useCallback(async () => {
    const setupList = [linkedSetupA?.setupData, linkedSetupB?.setupData].filter(Boolean);
    const setup = setupList.length === 2 ? setupList
                : setupList.length === 1 ? setupList[0]
                : null;
    // Carica storico sessioni per questa pista
    let history = null;
    if (teamId && sessionMeta?.track) {
      try {
        const slug = trackNameToSlug(sessionMeta.track);
        const allSessions = await getRecentSessions({ teamId, trackSlug: slug, n: 20 });
        // Filtra per pilota se disponibile
        const sessions = sessionMeta.driverName
          ? allSessions.filter(s => s.driverName === sessionMeta.driverName)
          : allSessions;
        history = buildHistoryContext(sessions.length ? sessions : allSessions);
        setSessionHistory(allSessions);
      } catch (_) {}
    }
    return { setup, history };
  }, [linkedSetupA, linkedSetupB, teamId, sessionMeta]);

  // ── Helper: salva sessione dopo coaching ───────────────────────
  const saveCoachedSession = useCallback(async (coachingResult, coachingType) => {
    if (!teamId || !coachingContext || !trackData) return;
    try {
      await saveRaceSession({
        teamId,
        trackSlug:      trackNameToSlug(sessionMeta?.track || trackData.track || ''),
        trackName:      sessionMeta?.track || trackData.track || '',
        driverName:     sessionMeta?.driverName || trackData.session || '',
        date:           sessionMeta?.date || '',
        lapTime_s:      lapTimeToSeconds(trackData.best_lap_time),
        lapTimeStr:     trackData.best_lap_time,
        refLapTimeStr:  trackData.reference_lap_time || null,
        sectors:        trackData.sectors,
        coachingType,
        summary:        coachingResult.sommario || '',
        findings:       coachingResult.consigli || [],
        setupA:         linkedSetupA?.setupData || null,
        setupB:         linkedSetupB?.setupData || null,
        weather:        weather,
        vehicleName:    sessionMeta?.vehicle || null,
      });
    } catch (_) {}
  }, [teamId, coachingContext, trackData, sessionMeta, linkedSetupA, linkedSetupB, weather]);

  // ── Intercetta coaching: mostra disclaimer se necessario ──────
  const requireDisclaimer = useCallback(async (type) => {
    const userId = getAuth().currentUser?.uid;
    const already = await hasAcceptedDisclaimer(userId).catch(() => false);
    if (already) {
      if (type === 'guida') runCoachGuida();
      else runCoachSetup();
    } else {
      setPendingCoachType(type);
      setDisclaimerOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDisclaimerAccept = useCallback(() => {
    setDisclaimerOpen(false);
    if (pendingCoachType === 'guida') runCoachGuida();
    else if (pendingCoachType === 'setup') runCoachSetup();
    setPendingCoachType(null);
  }, [pendingCoachType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDisclaimerCancel = useCallback(() => {
    setDisclaimerOpen(false);
    setPendingCoachType(null);
  }, []);

  // ── Step 2a: Consigli Guida ────────────────────────────────────
  const handleCoachGuida = useCallback(() => requireDisclaimer('guida'), [requireDisclaimer]);

  const runCoachGuida = useCallback(async () => {
    if (!coachingContext) return;
    setIsCoachingGuida(true);
    setError(null);
    try {
      const { history } = await buildCoachArgs();
      const result = await recoach({
        sectors:         coachingContext.sectors,
        session_summary: coachingContext.session_summary,
        weather:         coachingContext.weather,
        driver_feedback: [],
        setup:           null,
        coaching_type:   'guida',
        history,
        language:        i18n.language?.startsWith('en') ? 'en' : 'it',
      });
      setCoaching(result.coaching);
      await saveCoachedSession(result.coaching, 'guida');
      logAudit(getAuth().currentUser?.uid, teamId, AUDIT_ACTIONS.AI_COACHING, { type: 'guida', track: coachingContext?.session_summary?.track });
    } catch (err) {
      setError(err.message ?? 'Errore coaching Guida.');
    } finally {
      setIsCoachingGuida(false);
    }
  }, [coachingContext, buildCoachArgs, saveCoachedSession]);

  // ── Step 2b: Consigli Setup ────────────────────────────────────
  const handleCoachSetup = useCallback(() => requireDisclaimer('setup'), [requireDisclaimer]);

  const runCoachSetup = useCallback(async () => {
    if (!coachingContext) return;
    setIsCoachingSetup(true);
    setError(null);
    try {
      const { setup, history } = await buildCoachArgs();
      const result = await recoach({
        sectors:         coachingContext.sectors,
        session_summary: coachingContext.session_summary,
        weather:         coachingContext.weather,
        driver_feedback: [],
        setup,
        coaching_type:   'setup',
        history,
        language:        i18n.language?.startsWith('en') ? 'en' : 'it',
      });
      setCoaching(result.coaching);
      await saveCoachedSession(result.coaching, 'setup');
      logAudit(getAuth().currentUser?.uid, teamId, AUDIT_ACTIONS.AI_COACHING, { type: 'setup', track: coachingContext?.session_summary?.track });
    } catch (err) {
      setError(err.message ?? 'Errore coaching Setup.');
    } finally {
      setIsCoachingSetup(false);
    }
  }, [coachingContext, buildCoachArgs, saveCoachedSession]);

  const handleSectorClick = useCallback((n) => {
    setActiveSector(prev => prev === n ? null : n);
  }, []);

  const handleHoverPoint = useCallback((lat, lon) => {
    setHoveredPoint(lat != null && lon != null ? { lat, lon } : null);
  }, []);

  const handlePinPoint = useCallback((lat, lon) => {
    setPinnedPoint(lat != null && lon != null ? { lat, lon } : null);
  }, []);

  const handlePrint = useCallback(async () => {
    if (!trackData) return;
    try {
      const [{ pdf }, { default: RaceEngineerPdf }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./RaceEngineerPdf'),
      ]);
      const blob = await pdf(
        <RaceEngineerPdf trackData={trackData} coaching={coaching} weather={weather} session={sessionMeta} telemetry={telemetry} language={i18n.language?.startsWith('en') ? 'en' : 'it'} showWatermark={isTrial} />
      ).toBlob();
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = `race-engineer-${trackData.track || 'analisi'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF error:', e);
      setError(`Errore PDF: ${e?.message || e}`);
    }
  }, [trackData, coaching, weather, sessionMeta]);

  const hasData = !!trackData;

  // ── Modalità manutenzione ─────────────────────────────────────
  if (RACE_ENGINEER_MAINTENANCE) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 640,
            width: '100%',
            p: { xs: 3, md: 4 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
            <BuildIcon sx={{ fontSize: 48, color: 'warning.main' }} />
            <Chip label={t('maintenance.badge')} color="warning" size="small" sx={{ fontWeight: 700 }} />
          </Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('maintenance.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('maintenance.subtitle')}
          </Typography>
          <Alert severity="info" sx={{ textAlign: 'left', mt: 2 }}>
            {t('maintenance.details')}
          </Alert>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
            {t('maintenance.footer')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box id="re-printable" sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>

      {/* ── Header ───────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, md: 3 }, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SpeedIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('page.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {sessionMeta ? `${sessionMeta.track || ''} · ${sessionMeta.date || ''}` : t('page.subtitle_empty')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {weather?.temperatura_aria_c != null && (
            <Box data-noprint="true" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThermostatIcon sx={{ fontSize: 15, color: 'warning.main' }} />
              <Typography variant="caption">{weather.temperatura_aria_c}°C · {weather.condizione}</Typography>
              <AirIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="caption">{weather.vento_kmh}km/h</Typography>
            </Box>
          )}
          {sessionMeta?.track && (
            <Tooltip title={t('page.session_history')} data-noprint="true">
              <IconButton size="small" onClick={() => setHistoryOpen(true)}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasData && (
            <Tooltip title={t('page.export_pdf')} data-noprint="true">
              <IconButton size="small" onClick={handlePrint}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Upload: piena larghezza ──────────────────────────── */}
      <Box data-noprint="true" sx={{ mb: 2.5 }}>
        <SessionSetup
          teamId={teamId}
          onLoad={handleLoad}
          onTrackSelect={handleTrackSelect}
          onCoachGuida={handleCoachGuida}
          onCoachSetup={handleCoachSetup}
          isLoading={isLoading}
          isCoachingGuida={isCoachingGuida}
          isCoachingSetup={isCoachingSetup}
          hasData={hasData}
        />
      </Box>

      {/* ── Tutto a colonna intera ───────────────────────────── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Coach AI — sopra la mappa */}
        <Box className="coaching-panel-print-break">
          <CoachingPanel
            coaching={coaching}
            isLoading={isCoachingGuida || isCoachingSetup}
            activeSector={activeSector}
          />
        </Box>

        {/* Mappa satellite interattiva */}
        <TrackMapLeaflet
          trackData={trackData ?? (previewTrackGps ? { track_gps: previewTrackGps, sectors: [], reference_gps: [] } : null)}
          activeSector={activeSector}
          onSectorClick={handleSectorClick}
          hoveredPoint={hoveredPoint}
          pinnedPoint={pinnedPoint}
          telemetry={telemetry}
          activeLapNums={activeLapNums}
        />

        {/* Grafici telemetria con selettore canali e giri */}
        {telemetry && (
          <TelemetryCharts
            telemetry={telemetry}
            sectors={trackData?.sectors}
            onHoverPoint={handleHoverPoint}
            onPinPoint={handlePinPoint}
            activeLapNums={activeLapNums}
            onActiveLapNumsChange={setActiveLapNums}
          />
        )}

        {hasData && (
          <SectorTable
            sectors={trackData.sectors}
            activeSector={activeSector}
            onRowClick={handleSectorClick}
          />
        )}

        {hasData && (
          <DriverFeedback
            sectors={trackData.sectors}
            coachingContext={coachingContext}
            onRecoach={setCoaching}
          />
        )}

      </Box>

      <SessionHistory
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        teamId={teamId}
        trackSlug={trackNameToSlug(sessionMeta?.track || '')}
        trackName={sessionMeta?.track}
      />

      <AiDisclaimerDialog
        open={disclaimerOpen}
        onAccept={handleDisclaimerAccept}
        onCancel={handleDisclaimerCancel}
        userId={getAuth().currentUser?.uid}
        teamId={teamId}
      />

      <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} data-noprint="true">
        <Alert severity="error" onClose={() => setError(null)} variant="filled">{error}</Alert>
      </Snackbar>

      <AiQuotaExceededDialog
        open={quotaDialogOpen}
        onClose={() => setQuotaDialogOpen(false)}
        quota={quotaInfo}
      />
    </Box>
  );
}
